// Pure schedule-health / rollup logic shared by server and client.
// Mirrors the conditional-formatting rules from the original Excel tracker.

import type { ScheduleHealth } from "./types";

export interface ActivityCalcInput {
  status: string;
  percentComplete: number; // 0..100
  startDate: string | null;
  endDate: string | null;
  weightage: number;
  plannedCost: number;
  actualCost: number;
  statusMeta?: { treatAs?: "done" | "cancelled" | "in_progress" | "not_started" } | null;
}

const DAY = 86400000;

export function parseDate(d: string | null): number | null {
  if (!d) return null;
  const t = Date.parse(d.length <= 10 ? `${d}T00:00:00Z` : d);
  return Number.isNaN(t) ? null : t;
}

export function todayUTC(now = Date.now()): number {
  return Math.floor(now / DAY) * DAY;
}

/**
 * Schedule health, following the workbook legend:
 *  - cancelled  : status treated as cancelled
 *  - done       : 100% complete, finished on/after due date
 *  - ahead      : 100% complete, finished before due date
 *  - delayed    : past due date and not complete
 *  - planned    : not started and start date in the future (or no progress yet)
 *  - on_track   : anything else in progress within schedule
 */
export function scheduleHealth(a: ActivityCalcInput, now = Date.now()): ScheduleHealth {
  const treatAs = a.statusMeta?.treatAs;
  const today = todayUTC(now);
  const end = parseDate(a.endDate);
  const start = parseDate(a.startDate);
  const pct = clamp(a.percentComplete, 0, 100);

  if (treatAs === "cancelled") return "cancelled";
  if (pct >= 100 || treatAs === "done") {
    if (end !== null && end < today) return "ahead";
    return "done";
  }
  if (end !== null && end < today) return "delayed";
  if (pct <= 0 && (treatAs === "not_started" || (start !== null && start > today))) return "planned";
  if (pct <= 0 && start === null) return "planned";
  return "on_track";
}

export function weightedProgress(weightage: number, percentComplete: number): number {
  return (weightage || 0) * clamp(percentComplete, 0, 100) / 100;
}

export function durationDays(startDate: string | null, endDate: string | null): number | null {
  const s = parseDate(startDate);
  const e = parseDate(endDate);
  if (s === null || e === null) return null;
  return Math.round((e - s) / DAY) + 1;
}

export interface Rollup {
  totalActivities: number;
  overallPercent: number;
  plannedBudget: number;
  actualCost: number;
  budgetVariance: number;
  percentBudgetUsed: number;
  counts: Record<ScheduleHealth, number>;
}

export function rollup(activities: ActivityCalcInput[], now = Date.now()): Rollup {
  const counts: Record<ScheduleHealth, number> = {
    on_track: 0, ahead: 0, delayed: 0, cancelled: 0, planned: 0, done: 0,
  };
  let wSum = 0;
  let wProg = 0;
  let planned = 0;
  let actual = 0;
  for (const a of activities) {
    counts[scheduleHealth(a, now)]++;
    wSum += a.weightage || 0;
    wProg += weightedProgress(a.weightage || 0, a.percentComplete);
    planned += a.plannedCost || 0;
    actual += a.actualCost || 0;
  }
  return {
    totalActivities: activities.length,
    overallPercent: wSum > 0 ? round1((wProg / wSum) * 100) : 0,
    plannedBudget: round2(planned),
    actualCost: round2(actual),
    budgetVariance: round2(planned - actual),
    percentBudgetUsed: planned > 0 ? round1((actual / planned) * 100) : 0,
    counts,
  };
}

/** Which weekly Gantt columns a bar spans, given the grid's first Monday. */
export function ganttSpan(
  ganttStart: string | null,
  weeks: number,
  startDate: string | null,
  endDate: string | null,
): { fromWeek: number; toWeek: number } | null {
  const gs = parseDate(ganttStart);
  const s = parseDate(startDate);
  const e = parseDate(endDate);
  if (gs === null || s === null || e === null) return null;
  const fromWeek = Math.floor((s - gs) / (7 * DAY));
  const toWeek = Math.floor((e - gs) / (7 * DAY));
  if (toWeek < 0 || fromWeek > weeks - 1) return null;
  return { fromWeek: Math.max(0, fromWeek), toWeek: Math.min(weeks - 1, toWeek) };
}

export const HEALTH_LABEL: Record<ScheduleHealth, string> = {
  on_track: "On Track",
  ahead: "Ahead",
  delayed: "Delayed",
  cancelled: "Cancelled",
  planned: "Planned",
  done: "Completed",
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, Number.isFinite(n) ? n : 0));
}
function round1(n: number): number { return Math.round(n * 10) / 10; }
function round2(n: number): number { return Math.round(n * 100) / 100; }
